"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const lucide_react_1 = require("lucide-react");
const GoogleAuth = ({ onAuthChange }) => {
    const [authStatus, setAuthStatus] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(true);
    const [actionLoading, setActionLoading] = (0, react_1.useState)(false);
    const [message, setMessage] = (0, react_1.useState)(null);
    // Načti aktuální stav autentifikace
    const fetchAuthStatus = async () => {
        try {
            const response = await fetch('/api/v1/auth/google/status');
            if (response.ok) {
                const status = await response.json();
                setAuthStatus(status);
                onAuthChange?.(status.authenticated);
            }
            else {
                console.error('Chyba při načítání auth status');
            }
        }
        catch (error) {
            console.error('Chyba při načítání auth status:', error);
        }
        finally {
            setLoading(false);
        }
    };
    // Spusť Google OAuth přihlášení
    const handleGoogleLogin = async () => {
        setActionLoading(true);
        setMessage(null);
        try {
            const response = await fetch('/api/v1/auth/google/login');
            if (response.ok) {
                const data = await response.json();
                // Přesměruj na Google OAuth
                window.location.href = data.auth_url;
            }
            else {
                const error = await response.json();
                setMessage({ type: 'error', text: error.detail || 'Chyba při přihlašování' });
            }
        }
        catch (error) {
            setMessage({ type: 'error', text: 'Chyba při komunikaci se serverem' });
        }
        finally {
            setActionLoading(false);
        }
    };
    // Odhlášení z Google
    const handleGoogleLogout = async () => {
        setActionLoading(true);
        setMessage(null);
        try {
            const response = await fetch('/api/v1/auth/google/logout', { method: 'POST' });
            if (response.ok) {
                const data = await response.json();
                setMessage({ type: 'success', text: data.message });
                await fetchAuthStatus(); // Refresh status
            }
            else {
                const error = await response.json();
                setMessage({ type: 'error', text: error.detail || 'Chyba při odhlašování' });
            }
        }
        catch (error) {
            setMessage({ type: 'error', text: 'Chyba při komunikaci se serverem' });
        }
        finally {
            setActionLoading(false);
        }
    };
    // FORCE Odhlášení z Google - agresivní odstranění všech credentials
    const handleForceLogout = async () => {
        if (!confirm('🚨 FORCE LOGOUT\n\nTato akce agresivně smaže VŠECHNY Google credentials a cache soubory.\n\nOpravdu pokračovat?')) {
            return;
        }
        setActionLoading(true);
        setMessage(null);
        try {
            const response = await fetch('/api/v1/auth/google/force-logout', { method: 'POST' });
            if (response.ok) {
                const data = await response.json();
                setMessage({
                    type: data.success ? 'success' : 'error',
                    text: data.message
                });
                await fetchAuthStatus(); // Refresh status
                // Pokud bylo úspěšné, refreshuj celou stránku
                if (data.success) {
                    setTimeout(() => {
                        window.location.reload();
                    }, 2000);
                }
            }
            else {
                const error = await response.json();
                setMessage({ type: 'error', text: error.detail || 'Chyba při force odhlašování' });
            }
        }
        catch (error) {
            setMessage({ type: 'error', text: 'Chyba při komunikaci se serverem' });
        }
        finally {
            setActionLoading(false);
        }
    };
    // Obnovení YouTube cookies
    const handleRefreshCookies = async () => {
        setActionLoading(true);
        setMessage(null);
        try {
            const response = await fetch('/api/v1/auth/google/refresh-cookies', { method: 'POST' });
            if (response.ok) {
                const data = await response.json();
                setMessage({
                    type: data.success ? 'success' : 'error',
                    text: data.message
                });
            }
            else {
                const error = await response.json();
                setMessage({ type: 'error', text: error.detail || 'Chyba při obnovování cookies' });
            }
        }
        catch (error) {
            setMessage({ type: 'error', text: 'Chyba při komunikaci se serverem' });
        }
        finally {
            setActionLoading(false);
        }
    };
    // Zpracuj OAuth callback výsledky (pokud jsou v URL)
    (0, react_1.useEffect)(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const authSuccess = urlParams.get('auth');
        const error = urlParams.get('error');
        const errorMessage = urlParams.get('message');
        if (authSuccess === 'success') {
            // Úspěšné přihlášení
            setMessage({ type: 'success', text: 'Přihlášení úspěšné! YouTube cookies vygenerovány.' });
            // Vyčisti URL
            window.history.replaceState({}, document.title, window.location.pathname);
            fetchAuthStatus();
        }
        else if (error) {
            // Chyba při přihlášení
            let errorText = 'Chyba při přihlášení';
            if (error === 'oauth_not_configured') {
                errorText = 'Google OAuth není nakonfigurován';
            }
            else if (error === 'oauth_failed' && errorMessage) {
                errorText = `OAuth chyba: ${decodeURIComponent(errorMessage)}`;
            }
            else if (error === 'oauth_error' && errorMessage) {
                errorText = `Chyba při zpracování: ${decodeURIComponent(errorMessage)}`;
            }
            setMessage({ type: 'error', text: errorText });
            // Vyčisti URL
            window.history.replaceState({}, document.title, window.location.pathname);
            fetchAuthStatus();
        }
        else {
            // Standardní načtení bez callback parametrů
            fetchAuthStatus();
        }
    }, []);
    if (loading) {
        return ((0, jsx_runtime_1.jsx)("div", { className: "bg-white rounded-lg border border-gray-200", children: (0, jsx_runtime_1.jsx)("div", { className: "p-6", children: (0, jsx_runtime_1.jsxs)("div", { className: "flex items-center justify-center", children: [(0, jsx_runtime_1.jsx)("div", { className: "w-6 h-6 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" }), (0, jsx_runtime_1.jsx)("span", { className: "ml-2 text-gray-600", children: "Na\u010D\u00EDt\u00E1m stav p\u0159ihl\u00E1\u0161en\u00ED..." })] }) }) }));
    }
    if (!authStatus?.google_configured) {
        return ((0, jsx_runtime_1.jsx)("div", { className: "bg-white rounded-lg border border-gray-200", children: (0, jsx_runtime_1.jsx)("div", { className: "p-6", children: (0, jsx_runtime_1.jsxs)("div", { className: "flex items-center p-4 bg-yellow-50 border border-yellow-200 rounded-lg", children: [(0, jsx_runtime_1.jsx)(lucide_react_1.AlertCircle, { className: "w-5 h-5 text-yellow-600 mr-3" }), (0, jsx_runtime_1.jsxs)("div", { className: "text-yellow-800", children: [(0, jsx_runtime_1.jsx)("p", { className: "font-medium", children: "Google OAuth nen\u00ED nakonfigurov\u00E1n" }), (0, jsx_runtime_1.jsx)("p", { className: "text-sm", children: "Kontaktujte administr\u00E1tora pro nastaven\u00ED GOOGLE_CLIENT_ID a GOOGLE_CLIENT_SECRET." })] })] }) }) }));
    }
    return ((0, jsx_runtime_1.jsx)("div", { className: "bg-white rounded-lg border border-gray-200", children: (0, jsx_runtime_1.jsxs)("div", { className: "p-6", children: [(0, jsx_runtime_1.jsx)("h3", { className: "text-lg font-semibold text-gray-900 mb-4", children: "Google P\u0159ihl\u00E1\u0161en\u00ED pro YouTube" }), message && ((0, jsx_runtime_1.jsxs)("div", { className: `flex items-center p-4 mb-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 border border-green-200' :
                        message.type === 'error' ? 'bg-red-50 border border-red-200' :
                            'bg-blue-50 border border-blue-200'}`, children: [message.type === 'success' && (0, jsx_runtime_1.jsx)(lucide_react_1.CheckCircle, { className: "w-5 h-5 text-green-600 mr-3" }), message.type === 'error' && (0, jsx_runtime_1.jsx)(lucide_react_1.AlertCircle, { className: "w-5 h-5 text-red-600 mr-3" }), message.type === 'info' && (0, jsx_runtime_1.jsx)(lucide_react_1.Info, { className: "w-5 h-5 text-blue-600 mr-3" }), (0, jsx_runtime_1.jsx)("div", { className: `${message.type === 'success' ? 'text-green-800' :
                                message.type === 'error' ? 'text-red-800' :
                                    'text-blue-800'}`, children: message.text })] })), authStatus.authenticated ? ((0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsxs)("div", { className: "flex items-center p-4 mb-4 bg-green-50 border border-green-200 rounded-lg", children: [(0, jsx_runtime_1.jsx)(lucide_react_1.CheckCircle, { className: "w-5 h-5 text-green-600 mr-3" }), (0, jsx_runtime_1.jsxs)("div", { className: "text-green-800", children: [(0, jsx_runtime_1.jsx)("p", { className: "font-medium", children: "\u2705 P\u0159ihl\u00E1\u0161en p\u0159es Google \u00FA\u010Det" }), authStatus.user_info?.expires_at && ((0, jsx_runtime_1.jsxs)("p", { className: "text-sm", children: ["Platnost do: ", new Date(authStatus.user_info.expires_at).toLocaleString()] }))] })] }), (0, jsx_runtime_1.jsxs)("div", { className: "flex flex-wrap gap-2", children: [(0, jsx_runtime_1.jsxs)("button", { className: "flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed", onClick: handleRefreshCookies, disabled: actionLoading, children: [(0, jsx_runtime_1.jsx)(lucide_react_1.RefreshCw, { className: "w-4 h-4 mr-2" }), "Obnovit YouTube Cookies"] }), (0, jsx_runtime_1.jsxs)("button", { className: "flex items-center px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed", onClick: handleGoogleLogout, disabled: actionLoading, children: [(0, jsx_runtime_1.jsx)(lucide_react_1.LogOut, { className: "w-4 h-4 mr-2" }), "Odhl\u00E1sit se"] }), (0, jsx_runtime_1.jsxs)("button", { className: "flex items-center px-3 py-2 text-xs font-medium text-orange-600 bg-white border border-orange-300 rounded-lg hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed", onClick: handleForceLogout, disabled: actionLoading, title: "Agresivn\u00ED odstran\u011Bn\u00ED v\u0161ech Google credentials a cache", children: [(0, jsx_runtime_1.jsx)(lucide_react_1.AlertCircle, { className: "w-3 h-3 mr-1" }), "FORCE Logout"] })] })] })) : ((0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsx)("p", { className: "text-gray-600 text-sm mb-4", children: "P\u0159ihlaste se p\u0159es Google \u00FA\u010Det pro lep\u0161\u00ED p\u0159\u00EDstup k YouTube vide\u00EDm. Toto vy\u0159e\u0161\u00ED probl\u00E9my s \"Sign in to confirm you're not a bot\" chybami." }), (0, jsx_runtime_1.jsxs)("button", { className: "w-full flex items-center justify-center px-4 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed", onClick: handleGoogleLogin, disabled: actionLoading, children: [(0, jsx_runtime_1.jsx)("div", { className: "w-5 h-5 mr-2 bg-white rounded-full flex items-center justify-center", children: (0, jsx_runtime_1.jsx)("span", { className: "text-blue-600 text-xs font-bold", children: "G" }) }), actionLoading ? 'Přihlašuji...' : 'Přihlásit se přes Google'] })] })), actionLoading && ((0, jsx_runtime_1.jsx)("div", { className: "flex items-center justify-center mt-4", children: (0, jsx_runtime_1.jsx)("div", { className: "w-5 h-5 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" }) }))] }) }));
};
exports.default = GoogleAuth;
